/**
 * S3 Service (Lambda-backed)
 *
 * Secure S3 access via Lambda API. No credentials in frontend.
 * - list: calls list-files loader, returns files with pre-signed read URLs
 * - upload: gets pre-signed PUT URL from Lambda, uploads file directly to S3
 * - remove: calls _delete_file intent
 * - removeAllForEntity: calls _remove_all_files_for_entity intent
 *
 */

import { apiClient } from "~/services/apiClient";
import { INTENTS } from "~/utils";
import type { _ObjectType, S3FileWithUrl } from "~/types";

/** Result shape from list-files loader */
export interface FilesResult {
  all: S3FileWithUrl[];
  files: S3FileWithUrl[];
  images: S3FileWithUrl[];
}

export interface ListResult {
  success: boolean;
  data: FilesResult;
  error?: unknown;
}

export interface UploadResult {
  success: boolean;
  data: { Key?: string; Location?: string };
  error?: unknown;
}

export interface RemoveResult {
  success: boolean;
  data?: unknown;
  error?: unknown;
}

export const S3ServiceLambda = {
  async list({
    entityType,
    entityID,
  }: {
    entityType: _ObjectType;
    entityID: string;
  }): Promise<ListResult> {
    const result = await apiClient.loader<FilesResult>("list-files", {
      entityType,
      entityID,
    });

    if (!result.success || !result.data) {
      return {
        success: false,
        data: { all: [], files: [], images: [] },
        error: result.error,
      };
    }

    return { success: true, data: result.data };
  },

  async upload({
    entityType,
    entityID,
    file,
    name,
  }: {
    entityType: _ObjectType;
    entityID: string;
    file: File;
    name: string;
  }): Promise<UploadResult> {
    const urlResult = await apiClient.sendIntent<{
      uploadUrl: string;
      key: string;
    }>(INTENTS._get_upload_url, {
      entityType,
      entityID,
      filename: name,
    });

    if (!urlResult.success || !urlResult.data?.uploadUrl) {
      return {
        success: false,
        data: {},
        error: urlResult.error ?? "Failed to get upload URL",
      };
    }

    const uploadRes = await fetch(urlResult.data.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
    });

    if (!uploadRes.ok) {
      return {
        success: false,
        data: {},
        error: `Upload failed: ${uploadRes.status}`,
      };
    }

    return {
      success: true,
      data: { Key: urlResult.data.key },
    };
  },

  async remove({ key }: { key: string }): Promise<RemoveResult> {
    const result = await apiClient.sendIntent<{ deleted: boolean; key: string }>(
      INTENTS._delete_file,
      { key }
    );

    if (!result.success) {
      return { success: false, error: result.error };
    }

    return { success: true, data: result.data };
  },

  async removeAllForEntity({
    entityType,
    entityID,
  }: {
    entityType: _ObjectType;
    entityID: string;
  }): Promise<number> {
    const result = await apiClient.sendIntent<{ deleted: number }>(
      INTENTS._remove_all_files_for_entity,
      { entityType, entityID }
    );

    if (!result.success || result.data == null) {
      return 0;
    }

    return result.data.deleted ?? 0;
  },
};
